import type { ReactNode, SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function Icon({ size = 20, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  )
}

export function IconMap(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M9 4.5 3.5 6.5v13l5.5-2 6 2 5.5-2v-13L15 6.5 9 4.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="11.5" r="2.2" fill="currentColor" />
    </Icon>
  )
}

export function IconList(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M8 7h11M8 12h11M8 17h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="4.5" cy="7" r="1.2" fill="currentColor" />
      <circle cx="4.5" cy="12" r="1.2" fill="currentColor" />
      <circle cx="4.5" cy="17" r="1.2" fill="currentColor" />
    </Icon>
  )
}

export function IconChart(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="4" y="12" width="4" height="8" rx="1" fill="currentColor" />
      <rect x="10" y="8" width="4" height="12" rx="1" fill="currentColor" />
      <rect x="16" y="5" width="4" height="15" rx="1" fill="currentColor" />
    </Icon>
  )
}

export function IconHistory(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v4.5l3 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconSettings(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 3.5v2.2M12 18.3v2.2M4.8 7.2l1.9 1.1M17.3 15.7l1.9 1.1M4.8 16.8l1.9-1.1M17.3 8.3l1.9-1.1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </Icon>
  )
}

export function IconSearch(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconPin(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M12 21s-7-5.2-7-11a7 7 0 1 1 14 0c0 5.8-7 11-7 11z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.2" fill="currentColor" />
    </Icon>
  )
}

export function IconBolt(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M13 3 6 14h5l-1 7 8-12h-5l0-6z" fill="currentColor" />
    </Icon>
  )
}

export function IconSpeed(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M4.5 16a8.5 8.5 0 1 1 15 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path d="M12 16l4-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16" r="1.4" fill="currentColor" />
    </Icon>
  )
}

export function IconFuel(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="5" y="4" width="9" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M14 8h2.5a2 2 0 0 1 2 2v6.5a1.5 1.5 0 0 0 3 0V11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8 8h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconBattery(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="8" width="15" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M18 11v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <rect x="5.5" y="10.2" width="8" height="4.6" rx="1" fill="currentColor" />
    </Icon>
  )
}

export function IconOdo(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconTemp(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M10 14.5V6.5a2 2 0 1 1 4 0v8a3.2 3.2 0 1 1-4 0z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="16.5" r="1.3" fill="currentColor" />
    </Icon>
  )
}

export function IconUser(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5 19c0-3.2 3-5 7-5s7 1.8 7 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </Icon>
  )
}

export function IconLogout(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M10 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14 12H21M18 8l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
  )
}

export function IconRefresh(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M20 12a8 8 0 1 1-2.3-5.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path d="M20 4v5h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
  )
}

export function IconChevron(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
  )
}

export function IconBack(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M14 6 8 12l6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconCar(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M4 14h16l-1.5-5.2A2 2 0 0 0 16.6 7H7.4a2 2 0 0 0-1.9 1.8L4 14z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M4 14v3h2.5v-1h11v1H20v-3" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="7.5" cy="14" r="1.2" fill="currentColor" />
      <circle cx="16.5" cy="14" r="1.2" fill="currentColor" />
    </Icon>
  )
}

export function IconLang(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4.5 12h15M12 4c2.5 2.8 2.5 12.2 0 16M12 4c-2.5 2.8-2.5 12.2 0 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </Icon>
  )
}

export function IconInfo(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconPlate(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="8" width="18" height="8" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 12h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconBell(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M6 9.5a6 6 0 1 1 12 0c0 3.2 1.2 4.4 1.2 4.4H4.8S6 12.7 6 9.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M10 18a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconCrown(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M4 16.5 6.5 8l3.5 4L12 6.5 14 12l3.5-4L20 16.5H4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M5 18.5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconPlus(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </Icon>
  )
}

export function IconCompass(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="m14.5 9.5-2 5-5 2 2-5 5-2z" fill="currentColor" />
    </Icon>
  )
}

export function IconCalendar(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}

export function IconShare(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="18" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="6" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="18" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="m8.2 10.8 7.6-3.6M8.2 13.2l7.6 3.6" stroke="currentColor" strokeWidth="1.8" />
    </Icon>
  )
}

export function IconSupport(p: IconProps) {
  return (
    <Icon {...p}>
      <path
        d="M6 11a6 6 0 0 1 12 0v2a2 2 0 0 1-2 2h-1v-5a3 3 0 0 0-6 0v5H8a2 2 0 0 1-2-2v-2z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M9 18h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </Icon>
  )
}
